/**
 * Cancellation Reason Modal
 * Collects reason and optional notes when cancelling an event
 */

import React, { useState } from "react";
import Modal from "../common/Modal";
import Button from "../common/Button";
import { CANCELLATION_REASONS } from "../../types/event.types";

interface CancellationReasonModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: string, notes: string) => void;
  isLoading?: boolean;
  eventTitle?: string;
}

const CancellationReasonModal: React.FC<CancellationReasonModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading = false,
  eventTitle,
}) => {
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");

  const handleConfirm = () => {
    if (!reason) {
      return;
    }
    onConfirm(reason, notes);
  };

  const handleClose = () => {
    if (isLoading) {
      return;
    }
    setReason("");
    setNotes("");
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Cancel Event">
      <div className="space-y-4">
        {eventTitle && (
          <p className="text-sm text-gray-600">
            You are cancelling: <strong>{eventTitle}</strong>
          </p>
        )}

        {/* Smart Admin replacement R1 (2026-05-17) — explicit irreversibility
            warning. Cancellation can't be undone; a new event must be
            created if needed. Matches the BEFORE UPDATE DB trigger that
            blocks status change away from 'cancelled'. */}
        <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-900">
          <strong>This cannot be undone.</strong> Cancelled events are permanent
          and cannot be re-opened to a different status. If you need to run this
          event again, you'll need to create a new one.
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Cancellation Reason <span className="text-red-500">*</span>
          </label>
          <select
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Select a reason...</option>
            {CANCELLATION_REASONS.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Notes (optional)
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Additional details about the cancellation..."
          />
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={isLoading}
          >
            Go Back
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirm}
            disabled={!reason || isLoading}
          >
            {isLoading ? "Cancelling..." : "Cancel Event"}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default CancellationReasonModal;
